import { createQueryString, createQueryStringFormArray } from '@eika-infrastruktur/utils';

import type {
    Account,
    AccountBSUDetails,
    AccountId,
    AccountInterest,
    AccountNumber,
    AccountProperty,
    AccountWithdrawalDetails,
    AuthorizedCustomer,
    AuthorizedCustomerRequest,
    CompleteAccountProperty,
    ApprovalResult,
    TransactionDetails,
    TransactionId,
    TransactionList,
    SearchTransactionsRequest,
    UserId,
    ValidateAuthorizedCustomerRequest,
} from '../generated-types';
import sortAccounts from '../utils/mapper/accountSorter';
import { CONTEXT_PATH } from './index';
import { get, put, post, del } from './request';

export const ACCOUNT_SERVICE_PATH = 'rest/resource/accounts';

export async function getAccounts(): Promise<Account[]> {
    return get<Account[]>(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}`).then((accounts) => sortAccounts(accounts));
}

export async function getAccount(accountId: AccountId): Promise<Account> {
    const account = await get<Account>(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}`);
    return account ?? undefined;
}

export function getTransactions(
    accountId: AccountId,
    searchParams?: SearchTransactionsRequest,
    nextReference?: string,
): Promise<TransactionList> {
    if (searchParams) {
        return post<TransactionList>(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/transactions/search`, {
            ...searchParams,
            nextReference,
        });
    }
    return post<TransactionList>(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/transactions`, {
        nextReference,
    });
}

export function getTransaction(accountId: AccountId, transactionId: TransactionId): Promise<TransactionDetails> {
    return get<TransactionDetails>(
        `${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/transactions/${transactionId.value}`,
    );
}

export async function getAccountProperties(): Promise<AccountProperty[]> {
    return get(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/properties`);
}

export async function putAccountProperties(accountProperties: CompleteAccountProperty[]): Promise<unknown> {
    return put(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/properties`, accountProperties);
}

export async function updateAccountDetails(accountProperties: AccountProperty[]): Promise<unknown> {
    return put(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}`, accountProperties);
}

export async function updateAccountAlias(accountId: AccountId, alias: string): Promise<unknown> {
    const url = `${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/alias`;
    return put(url, { alias });
}

export async function getInterest(accountId: AccountId): Promise<AccountInterest> {
    return get<AccountInterest>(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/interest`);
}

export async function getAuthorizedCustomers(accountId: AccountId): Promise<AuthorizedCustomer[]> {
    return get<AuthorizedCustomer[]>(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/authorized-customers`);
}

export async function validateCustomerName(
    accountId: AccountId,
    validateAuthorizedCustomer: ValidateAuthorizedCustomerRequest,
): Promise<boolean> {
    return post<boolean>(
        `${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/authorized-customers/validate-name`,
        validateAuthorizedCustomer,
    );
}

export async function addAuthorizedCustomer(
    accountId: AccountId,
    authorizedCustomerRequest: AuthorizedCustomerRequest,
): Promise<ApprovalResult> {
    return post(
        `${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/authorized-customers`,
        authorizedCustomerRequest,
    );
}

export async function deleteAuthorizedCustomer(accountId: AccountId, customerId: UserId): Promise<void> {
    const params = createQueryString({
        customerNumber: customerId.value,
    });
    return del(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/authorized-customers/${params}`);
}

export function getTransactionPdfUrl(accountId: AccountId, transactionId: TransactionId) {
    return `${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/transactions/${transactionId.value}/pdf`;
}

export function getTransactionListDownloadUrl(
    accountId: AccountId,
    searchParams: SearchTransactionsRequest,
    fileType: 'PDF' | 'EXCEL' | 'CSV',
) {
    const { fromDate, toDate, category, maxAmount, minAmount, freetext } = searchParams;

    const params = createQueryString({
        fromDate,
        toDate,
        maxAmount: maxAmount?.toString(),
        minAmount: minAmount?.toString(),
        freetext,
    });
    const arrayQueryString = createQueryStringFormArray('category', category ?? [], params.length === 0);
    return `${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/transactions/${fileType}/download${params}${arrayQueryString}`;
}

export function getAccountFreeWithdrawals(accountId: AccountId) {
    return get<AccountWithdrawalDetails>(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/freewithdrawals`);
}

export function getBSUDetails(accountId?: AccountId) {
    return get<AccountBSUDetails>(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId?.value}/bsudetails`);
}

export function isCloseable(accountId: AccountId) {
    return get<boolean>(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}/closeable`);
}

export async function deleteAccount(accountId: AccountId, transferTo: AccountNumber): Promise<ApprovalResult> {
    const params = createQueryString({
        transferTo: transferTo?.value,
        deviceId: window.ReactNativeProps?.deviceId,
    });
    return del<ApprovalResult>(`${CONTEXT_PATH}/${ACCOUNT_SERVICE_PATH}/${accountId.value}${params}`);
}
